/**
 * Slicing a gauge: every needle follows the slicers, live, with no query.
 *
 * With slicers on, a gauge's response carries the readings behind its dials
 * (`readings`: time, each dial signal, each slicer column). A slicer move
 * recomputes every dial from the readings inside the slicer ranges - Latest,
 * Average, Sum, Min, Max, Count and Calculations alike - so dragging and Play
 * move the needles as they move the band.
 *
 * Past 20,000 readings the server thins them evenly (`sampled`). The numbers
 * are then close but not exact, so letting a slicer go asks once for the exact
 * figures (`settleGaugeSlices` in plot-actions) and they replace the estimate
 * until the slicer moves again.
 */
import type { GaugeDialResult, GaugeReadings, GaugeResponse } from '../../api/types.ts';
import { filters, type FilterRow } from '../../state/store.ts';
import { evaluateNumberFormula } from './number-formula.ts';
import { isNarrowed, slicerRows } from './slicers.ts';

/** The narrowed slicers, as text: equal text, equal slice. `[]` when none narrows. */
export function sliceSignature(rows: FilterRow[] = filters.value): string {
  return JSON.stringify(
    slicerRows(rows)
      .filter((r) => isNarrowed(r.slicer))
      .map((r) => [r.column, r.slicer.min, r.slicer.max]),
  );
}

/** Which readings every narrowed slicer keeps (1 = kept); null when none applies. */
export function readingMask(readings: GaugeReadings, rows: FilterRow[] = filters.value): Uint8Array | null {
  const active = slicerRows(rows).filter((r) => isNarrowed(r.slicer));
  const n = readings.t.length;
  let keep: Uint8Array | null = null;
  for (const r of active) {
    const values = readings.columns[r.column];
    if (!values) continue;
    keep ??= new Uint8Array(n).fill(1);
    const lo = r.slicer.min ?? -Infinity;
    const hi = r.slicer.max ?? Infinity;
    for (let i = 0; i < n; i++) {
      const v = values[i];
      if (v == null || !(v >= lo && v <= hi)) keep[i] = 0;
    }
  }
  return keep;
}

/** One aggregate over the kept readings, as the server computes it. */
export function summarise(
  readings: GaugeReadings, keep: Uint8Array, agg: string, column: string,
): { value: number | null; count: number } {
  const values = readings.columns[column];
  if (!values) return { value: null, count: 0 };
  let count = 0, sum = 0, lo = Infinity, hi = -Infinity, latest: number | null = null, latestT = -Infinity;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (!keep[i] || v == null) continue;
    count++; sum += v;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
    // Last by time; a tie goes to the greater value, as on the server.
    const t = readings.t[i] ?? -Infinity;
    if (t > latestT || (t === latestT && latest != null && v > latest)) { latestT = t; latest = v; }
  }
  const value = agg === 'count' ? count
    : !count ? null
    : agg === 'avg' ? sum / count
    : agg === 'sum' ? sum
    : agg === 'min' ? lo
    : agg === 'max' ? hi
    : latest;
  return { value, count };
}

/** Recompute each dial from the readings a mask keeps; a missing dial stays missing. */
export function recomputeDials(dials: GaugeDialResult[], readings: GaugeReadings, keep: Uint8Array): GaugeDialResult[] {
  return dials.map((d) => {
    if (d.missing.length) return d;
    if (d.key.startsWith('f:')) {
      let count = Infinity;
      const value = evaluateNumberFormula(d.key.slice(2), (fn, signal) => {
        const s = summarise(readings, keep, fn, signal);
        count = Math.min(count, s.count);
        return s.value;
      });
      return { ...d, value, valid_count: Number.isFinite(count) ? count : 0 };
    }
    const at = d.key.indexOf(':');
    const s = summarise(readings, keep, d.key.slice(0, at), d.key.slice(at + 1));
    return { ...d, value: s.value, valid_count: s.count };
  });
}

let exact: { source: GaugeResponse; signature: string; result: GaugeResponse } | null = null;

/** Keep exact figures fetched for one slice of one response (see `settleGaugeSlices`). */
export function setExactGauge(source: GaugeResponse, signature: string, result: GaugeResponse): void {
  exact = { source, signature, result };
}

/** Whether any ship's readings were thinned - the slice is then an estimate until settled. */
export function gaugeSampled(data: GaugeResponse): boolean {
  return !!data.readings?.sampled || !!data.ships?.some((s) => s.readings?.sampled);
}

/** `data` as the current slicers see it. The same object when nothing is sliced. */
export function sliceGauge(data: GaugeResponse, rows: FilterRow[] = filters.value): GaugeResponse {
  const signature = sliceSignature(rows);
  if (signature === '[]') return data;
  if (exact && exact.source === data && exact.signature === signature) return exact.result;
  const slice = <T extends { dials: GaugeDialResult[] | null; readings?: GaugeReadings }>(s: T): T => {
    if (!s.dials || !s.readings) return s;
    const keep = readingMask(s.readings, rows);
    return keep ? { ...s, dials: recomputeDials(s.dials, s.readings, keep) } : s;
  };
  if (data.ships) return { ...data, ships: data.ships.map(slice) };
  return slice(data);
}

/** A slicer column's values across every ship's readings, for the strip's range and histogram. */
export function gaugeColumnArrays(data: GaugeResponse | null | undefined, column: string): Array<ArrayLike<number | null>> {
  if (data?.plot_type !== 'gauge') return [];
  const all = data.ships ? data.ships.map((s) => s.readings) : [data.readings];
  return all.flatMap((r) => (r?.columns[column] ? [r.columns[column]!] : []));
}
